<template>
  <v-card>
    <v-card-title class="pa-0">
      <v-toolbar flat color="orange">
        <v-toolbar-title>{{ form.id ? '予定を変更する' : '予定を追加する' }}</v-toolbar-title>
        <v-tooltip v-if="form.id" bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon small
              v-on="on"
              class="mr-2"
              color="red"
              @click="remove">
              <v-icon small>fal fa-trash-alt</v-icon>
            </v-btn>
          </template>
          <span>削除する</span>
        </v-tooltip>
        <v-spacer />
        <v-row dense align="center" justify="end">
          <v-btn-toggle mandatory v-if="!isFixed">
            <v-btn
              :outlined="getCalendarType !== 'event'"
              :depressed="getCalendarType === 'event'"
              color="secondary white--text"
              value="event"
              @click="changeType('event')">顧客予定</v-btn>
            <v-btn
              :outlined="getCalendarType !== 'consult'"
              :depressed="getCalendarType === 'consult'"
              color="secondary white--text"
              value="consult"
              @click="changeType('consult')">相談予約</v-btn>
          </v-btn-toggle>
        </v-row>
        <v-dialog scrollable v-model="config.filer.active" max-width="800">
          <v-card class="px-0" width="100%">
            <v-card-text class="px-0">
              <filer v-if="config.filer.active && getRootMediaId"
                :rid="getRootMediaId" :fixedRoot="true"
                :filters="{ ids: getAttachmentItemIds }"
                @select="selectAttachments"
              />
            </v-card-text>
            <v-card-actions>
              <v-btn text color="secondary" @click.stop="closeFiler">閉じる</v-btn>
              <v-spacer></v-spacer>
              <v-btn depressed color="secondary" :disabled="!store.attachments.length" @click.stop="setAttachments">選択する</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-toolbar>
    </v-card-title>
    <v-card-text>
      <v-form lazy-validation ref="form" v-model="form.valid" @submit.prevent="onSubmit">
        <v-list dense>
          <v-list-item v-if="!isFixed">
            <v-list-item-avatar>
              <v-icon small>fa-user</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-row dense>
                <v-col cols="12">
                  <v-autocomplete
                    ref="firstInput"
                    v-model="store.cid"
                    label="予定を追加する顧客"
                    :items="getClientItems"
                    :loading="isSearching"
                    :search-input.sync="form.search"
                    :rules="rules.required"
                    @change="submit({ type: 'arrange' })"
                  />
                </v-col>
              </v-row>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-list dense v-if="store.cid">
          <v-list-item>
            <v-list-item-avatar>
              <v-icon small>fal fa-file-signature</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-row dense>
                <v-col cols="12">
                  <v-select
                    v-model="form.arrange"
                    label="留学の種類"
                    :items="getArrangeItems"
                    :loading="isLoading"
                  />
                </v-col>
              </v-row>
            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-avatar>
              <v-icon small>far fa-dot-circle</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-row dense>
                <v-col cols="12">
                  <v-combobox
                    v-model="form.category"
                    label="目的"
                    :readonly="!!form.id"
                    :items="getCategoryMetaItems"
                    :rules="rules.required"
                    @change="updateCategory"
                  />
                </v-col>
              </v-row>
            </v-list-item-content>
          </v-list-item>
          <v-list-item>
            <v-list-item-avatar>
              <v-icon small>fal fa-pencil</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.description"
                    label="備考"
                  />
                </v-col>
              </v-row>
            </v-list-item-content>
          </v-list-item>
          <v-subheader class="secondary white--text font-weight-bold">日程</v-subheader>
          <v-list-item v-for="(item, idx) in getScheduleItems" :key="item.id">
            <v-list-item-avatar>
              <v-icon small>fal fa-calendar-alt</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-row dense>
                <v-col cols="3">
                  <v-combobox
                    v-model="item.label"
                    label="ラベル"
                    :items="getLabelMetaItems"
                  />
                </v-col>
                <v-col cols="9" class="pl-2">
                  <v-menu offset-y
                    v-model="item.datepicker"
                    transition="scale-transition"
                    min-width="290px"
                    :close-on-content-click="false"
                    :nudge-right="40">
                    <template v-slot:activator="{ on }">
                      <v-text-field readonly
                        v-model="item.format"
                        v-on="on"
                        label="日付"
                        :rules="rules.required"
                        :append-icon="item.date ? 'fal fa-times' : null"
                        @click:append="item.date = ''"
                      />
                    </template>
                    <v-date-picker
                      v-model="item.date"
                      locale="ja"
                      color="amber"
                      :day-format="date => new Date(date).getUTCDate()"
                      @input="item.datepicker = false"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12">
                  <v-select chips multiple
                    v-model="item.reminder.minutes"
                    label="リマインダー"
                    :items="getReminderMetaItems"
                    :append-icon="idx ? 'fal fa-trash-alt' : null"
                    @click:append="removeSchedule(item.id)"
                  />
                </v-col>
                <v-col cols="12">
                  <v-subheader class="px-0">添付ファイル</v-subheader>
                </v-col>
                <v-col cols="12">
                  <v-chip v-for="jtem in item.attachments" :key="jtem.id" close @click:close="removeAttachment({ item, attachment: jtem })">{{ jtem.name }}</v-chip>
                  <v-chip color="secondary" @click="openFiler(item)">
                    <v-avatar left>
                      <v-icon small>fal fa-paperclip</v-icon>
                    </v-avatar>
                    追加する
                  </v-chip>
                </v-col>
              </v-row>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="!form.id" @click="addSchedule">
            <v-list-item-avatar>
              <v-icon small class="secondary--text">fal fa-plus</v-icon>
            </v-list-item-avatar>
            <v-list-item-content class="secondary--text">日程を追加する</v-list-item-content>
          </v-list-item>
        </v-list>
      </v-form>
      <confirm ref="confirmer" />
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions>
      <v-btn text
        color="secondary"
        @click.stop="$emit('close')">止める</v-btn>
      <v-spacer></v-spacer>
      <v-btn depressed
        v-if="!form.id"
        color="secondary"
        :disabled="!isValid"
        :loading="isLoading"
        @click.stop="insert">追加する</v-btn>
      <v-btn depressed
        v-if="form.id"
        color="secondary"
        :disabled="!isValid"
        :loading="isLoading"
        @click.stop="update">変更する</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { mask } from 'vue-the-mask'
import filer from '@/components/filers/client'
import confirm from '@/components/modals/confirm'
export default {
  name: 'sasmango-calendar-editor-client-card',
  directives: { mask },
  components: {
    filer,
    confirm
  },
  props: [ 'fixed', 'cid', 'arranges', 'date', 'type' ],
  data () {
    return {
      config: {
        status: {
          loading: false,
          active: false,
          searching: false,
          fixed: false
        },
        filer: {
          active: false
        },
        client: {
          selectable: false
        }
      },
      meta: {
        reminder: {
          d1: '1日前',
          d2: '2日前',
          w1: '1週前',
          w2: '2週前',
          w3: '3週前',
          w4: '4週前'
        }
      },
      store: {
        cid: '',
        attachments: [],
        selected: [],
        arranges: [],
        clients: []
      },
      form: {
        valid: true,
        id: '',
        search: null,
        client: '',
        arrange: '',
        category: '',
        summary: '',
        description: '',
        schedules: []
      },
      rules: {
        required: [
          v => !!v || '必須項目です'
        ]
      }
    }
  },
  created () {
    this.init()
  },
  methods: {
    onSubmit: function () { return false },
    init: function () {
      this.$set(this.config.status, 'fixed', this.$props.fixed)
    },
    insert: function () {
      this.submit({ type: 'insert' })
    },
    update: function () {
      this.submit({ type: 'update', eventId: this.form.id })
    },
    remove: function () {
      this.$refs.confirmer.open({
        title: '予定の削除',
        message: '登録済みの予定を削除してもよろしいでしょうか？',
        callback: () => { this.submit({ type: 'remove', eventId: this.form.id }) }
      })
    },
    preSubmit: function (req) {
      const { type, eventId } = req
      const { arrange, search, category, summary, description, schedules } = this.form
      const cid = this.store.cid
      const aid = this.$store.getters['author/get']('userid')
      const urls = {
        arrange: `${this.$url.api}client/get/arranges/${cid}`,
        client: `${this.$url.api}client/get/items`,
        insert: `${this.$url.api}calendar/insert/client/${cid}/`,
        update: `${this.$url.api}calendar/update/client/${cid}/${eventId}`,
        remove: `${this.$url.api}calendar/remove/item/${eventId}`,
        recurrence: `${this.$url.api}calendar/get/item/${eventId}`
      }
      const fds = {
        arrange: { all_required: true },
        client: { name: search },
        insert: { aid, arrange, category, description, schedules },
        update: { aid, arrange, category, description, schedule: schedules[0] },
        remove: { summary },
        recurrence: {}
      }
      if (!this.socket) {
        this.$set(this, 'socket', this.$store.getters['config/get']('socket'))
      }
      if (type && urls[type] && fds[type]) {
        if (cid && this.$refs.form && this.$refs.form.validate()) {
          this.$set(this.config.status, 'loading', true)
          return { type, url: urls[type], fd: fds[type] }
        } else if (type === 'client' && search) {
          this.$set(this.config.status, 'searching', true)
          return { type, url: urls[type], fd: fds[type] }
        } else if (type === 'arrange' && cid) {
          this.$set(this.config.status, 'loading', true)
          return { type, url: urls[type], fd: fds[type] }
        } else {
          return false
        }
      } else {
        return false
      }
    },
    submit: function (req) {
      const dt = this.preSubmit(req)
      if (dt) {
        const { type, url, fd } = dt
        this.$firebase.auth().currentUser.getIdToken(true).then(token => {
          this.axios.post(url, fd, {
            headers: {
              'firebase-token': token
            }
          }).then(res => {
            console.log(res)
            if (res.data && res.data.success) {
              this.postSubmit({
                type,
                data: res.data.data
              })
            } else {
              this.failSubmit()
            }
          }).catch(err => {
            console.log(err)
            this.failSubmit()
          })
        })
      }
    },
    postSubmit: function (req) {
      const socket = this.$store.getters['config/get']('socket')
      const { type, data } = req || {}
      if (type === 'arrange') {
        this.$set(this.config.status, 'loading', false)
        if (data.length) {
          this.$set(this.form, 'arrange', data[data.length - 1].type)
        }
      } else if (type === 'client') {
        this.$set(this.config.status, 'searching', false)
        this.$set(this.store, 'clients', data)
      }
      if (['insert', 'update', 'remove'].includes(type)) {
        if (!socket.connected) {
          socket.connect()
        }
        socket.emit('POST_CALENDAR', Object.assign(req, {
          user: this.$store.getters['author/get']('userid'),
          calendarType: 'events'
        }), (res) => {
          console.log(res)
          this.$set(this.config.status, 'loading', false)
          this.$emit('done', req)
          this.$emit('close')
        })
      }
    },
    failSubmit: function () {
      this.$set(this.config.status, 'loading', false)
    },
    emitSocket: function (req) {
    },
    openForInsert: function (req) {
      this.$set(this.store, 'cid', this.$props.cid || '')
      this.$set(this.config.status, 'fixed', false)
      this.$set(this, 'form', {
        valid: true,
        search: null,
        arrange: '',
        category: '',
        description: '',
        schedules: []
      })
      this.addSchedule({ label: '' })
      this.submit({ type: 'arrange' })
      setTimeout(() => {
        if (this.$refs.firstInput) {
          this.$refs.firstInput.focus()
        }
      }, 500)
    },
    openForUpdate: function (req) {
      const { id, summary, description, start, reminders, attachments, extendedProperties } = req || {
        id: '',
        summary: '',
        description: '',
        start: {},
        reminders: [],
        attachments: [],
        extendedProperties: {}
      }
      const { overrides } = reminders || {}
      const { cid, arrange, category, label, createdby } = extendedProperties.private || {}
      const schedules = [{
        label,
        date: start.date || start.dateTime || '',
        minutes: overrides ? overrides.map(jt => {
          return jt.minutes ? this.minutes2duration(jt.minutes) : ''
        }) : [],
        attachments: attachments || []
      }]
      this.$set(this.store, 'cid', cid || this.$props.cid || this.$route.params.id || '')
      this.$set(this.config.status, 'fixed', true)
      this.$set(this, 'form', {
        valid: true,
        id,
        search: null,
        arrange: arrange || '',
        category: category || '',
        summary: summary || '',
        description: description || '',
        schedules: []
      })
      if (schedules && schedules.length) {
        for (let i = 0; i < schedules.length; i++) {
          this.addSchedule(schedules[i])
        }
      }
      setTimeout(() => {
        if (this.$refs.firstInput) {
          this.$refs.firstInput.focus()
        }
      }, 500)
    },
    changeType: function (type) {
      if (type) {
        this.$emit('change', type)
      }
    },
    openFiler: function (item) {
      if (item && item.id) {
        this.$set(this.store, 'id', item.id)
        this.$set(this.store, 'selected', item.attachments.map(it => { return it.id }))
        this.$set(this.config.filer, 'active', true)
      }
    },
    closeFiler: function () {
      this.$set(this.config.filer, 'active', false)
      this.$set(this.store, 'id', '')
      this.$set(this.store, 'attachments', [])
    },
    searchClient: _.debounce(function (val) {
      this.submit({ type: 'client' })
    }, 300),
    selectAttachments: function (items) {
      let selects = items ? items.filter(it => {
        return (it.id && it.type && it.type !== 'folder')
      }) : []
      this.$set(this.store, 'attachments', selects)
    },
    setAttachments: function () {
      if (this.store.id) {
        let idx = this.form.schedules.findIndex(it => { return it.id === this.store.id })
        if (idx >= 0) {
          this.$set(this.form.schedules[idx], 'attachments', this.form.schedules[idx].attachments.concat(this.store.attachments))
        }
        this.closeFiler()
      }
    },
    removeAttachment: function (req) {
      const { item, attachment } = req || {}
      if (item && attachment && item.id && attachment.id) {
        let idx = this.form.schedules.findIndex(it => { return it.id === item.id })
        if (idx >= 0) {
          let jdx = this.form.schedules[idx].attachments.findIndex(it => { return it.id === attachment.id })
          if (jdx >= 0) {
            this.form.schedules[idx].attachments.splice(jdx, 1)
          }
        }
      }
    },
    updateCategory: function () {
      const { category } = this.form || {}
      const { labels } = this.$mango.reminders[category] || {}
      const { items, defaults } = labels || { items: [], defaults: [] }
      this.$set(this.form, 'schedules', [])
      if (defaults.length) {
        for (let i = 0; i < defaults.length; i++) {
          let def = defaults[i]
          this.addSchedule({ label: items[def] || '' })
        }
      } else {
        this.addSchedule({ label: '' })
      }
    },
    addSchedule: function (req) {
      const { label, date, minutes, attachments } = req || {}
      const eventDate = date || this.$props.date || ''
      this.form.schedules.push({
        id: this.$uuid(),
        label: label || '',
        date: eventDate ? this.$moment(eventDate).format('YYYY-MM-DD') : '',
        datepicker: false,
        reminder: {
          methods: 'email',
          minutes: minutes || []
        },
        attachments: attachments || []
      })
    },
    removeSchedule: function (uid) {
      if (uid) {
        let idx = this.form.schedules.findIndex(it => { return it.id === uid})
        if (idx >= 0) {
          this.form.schedules.splice(idx, 1)
        }
      }
    },
    minutes2duration: function (minutes) {
      if (minutes != null) {
        if (minutes >= 10080) {
          return `w${minutes / 10080}`
        } else if (minutes >= 1440) {
          return `d${minutes / 1440}`
        } else {
          return `min${minutes}`
        }
      } else {
        return ''
      }
    }
  },
  watch: {
    'form.search': function (newVal, oldVal) {
      if (newVal && oldVal && newVal.length < oldVal.length) {
        this.$set(this.store, 'clients', [])
      }
      if (!this.config.status.searching && !this.store.clients.length) {
        this.searchClient()
      }
    }
  },
  computed: {
    isLoading () {
      return this.config.status.loading
    },
    isActive () {
      return this.config.status.active
    },
    isValid () {
      return this.form.valid
    },
    isSearching () {
      return this.config.status.searching
    },
    isFixed () {
      return this.$props.fixed
    },
    getClientId () {
      return this.$props.cid
    },
    getCalendarType () {
      return this.$props.type
    },
    isClientSelectable () {
      return this.config.client.selectable
    },
    getClientItems () {
      return this.store.clients.length ? this.store.clients.map(it => {
        return {
          text: `${it.name.replace(',', '')}（${it.kana.replace(',', '')}）`,
          value: it.id
        }
      }) : []
    },
    getCategoryMetaItems () {
      return Object.keys(this.$mango.reminders)
    },
    getLabelMetaItems () {
      return this.$mango.reminders[this.form.category] ? this.$mango.reminders[this.form.category].labels.items : []
    },
    getArrangeItems () {
      return [{ text: '該当なし', value: '' }].concat(Object.keys(this.$mango.arrange.types).map(key => {
        return {
          text: this.$mango.arrange.types[key],
          value: key
        }
      }))
    },
    getTimeItems () {
      return [...Array(24 * 2).keys()].map(i => {
        return `${('00' + Math.floor(i / 2)).slice(-2)}:${('00' + (i % 2) * 30).slice(-2)}`
      })
    },
    getScheduleItems () {
      return this.form.schedules.length ? this.form.schedules.map(it => {
        return Object.assign(it, {
          format: it.date ? this.$moment(it.date).format('YYYY/MM/DD') : ''
        })
      }) : []
    },
    getReminderItems () {
      return this.form.reminders
    },
    getReminderMetaItems () {
      return Object.keys(this.meta.reminder).map(key => {
        return {
          text: this.meta.reminder[key],
          value: key
        }
      })
    },
    getAttachmentItemIds () {
      return this.store.selected
    },
    getRootMediaId () {
      return this.$store.getters['client/get']('lib').media
    },
    printFormStartDate () {
      return (
        this.form &&
        this.form.start.date
      ) ? this.$moment(this.form.start.date).format('YYYY/MM/DD') : ''
    },
    printFormEndDate () {
      return (
        this.form &&
        this.form.end.date
      ) ? this.$moment(this.form.end.date).format('YYYY/MM/DD') : ''
    }
  }
}
</script>